import React from 'react';

import { FileUpload } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import _ from 'es-toolkit/compat';
import type { Accept } from 'react-dropzone';

import { filePickerFileTypes } from '@clearverify/constants';

import { FilePicker } from 'src/components/common';
import type { SxProp } from 'src/types/mui';

type FormFileUploadProps = {
  label?: string;
  disabled?: boolean;
  name?: string;
  accept?: Accept | string;
  children?: React.ReactNode;
  filePickerSx?: SxProp;
  onChange: (files: File[]) => void;
  maxSize?: number;
  maxFiles?: number;
};

export const FileUploadField: React.FC<FormFileUploadProps> = ({
  label,
  name,
  disabled,
  accept: acceptProp,
  children,
  filePickerSx,
  onChange,
  maxSize,
  maxFiles = 1,
}) => {
  const accept = React.useMemo((): Accept | undefined => {
    if (_.isString(acceptProp)) {
      // TODO: There are still many places in the application where just a string is passed here
      // eslint-disable-next-line guard-for-in
      for (const fileType in filePickerFileTypes) {
        const extension = (filePickerFileTypes as any)[fileType];
        const extensionKey = Object.keys(extension)[0];
        if (extensionKey && extension[extensionKey].includes(acceptProp)) {
          return extension[extensionKey];
        }
      }

      return undefined;
    }

    return acceptProp;
  }, [acceptProp]);

  return (
    <Box sx={styles.container}>
      {label && (
        <Typography variant="body2" color="textSecondary">
          {label}
        </Typography>
      )}
      <FilePicker
        name={name}
        onChange={onChange}
        maxFiles={maxFiles}
        accept={accept}
        disabled={disabled}
        containerSx={filePickerSx}
        maxSize={maxSize}
      >
        {children || (
          <IconButton color="default" size="medium" sx={styles.button}>
            <FileUpload fontSize="small" sx={styles.buttonIcon} />
            <Typography sx={styles.buttonLabel}>UPLOAD FILE</Typography>
          </IconButton>
        )}
      </FilePicker>
    </Box>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  button: {
    marginLeft: '15px',
    borderRadius: '4px',
  },
  buttonIcon: {
    color: '#333333',
    marginRight: '5px',
  },
  buttonLabel: {
    color: '#333333',
    fontSize: '14px',
  },
};
